import { isOfficial, PublicSettings } from '@witkac/kartamiejska-shared';
import { RouteConfig } from 'vue-router';

import BenefitsHistory from '@/components/BenefitUsageHistory.vue';
import EntityHistory from '@/components/EntityHistory.vue';
import store from '@/store/store';
import { baseFilters } from '@/utils/baseFilters';
import PartnerEmployees from '@/views/employees/List.vue';
import EmployeeProfile from '@/views/employees/Profile.vue';
import PartnerProfile from '@/views/partners/Detail.vue';
import Partners from '@/views/partners/List.vue';
import PartnerView from '@/views/partners/PartnerView.vue';
import Benefits from '@/views/partners/tabs/Benefits.vue';
import Places from '@/views/partners/tabs/Places.vue';
import UserRoles from '@/views/roles/roleAddUser.vue';

export const partnerListRoute: RouteConfig = {
  path: 'partners',
  name: 'partners',
  component: Partners,
  beforeEnter: (to, from, next) => {
    store
      .dispatch('partners/list', baseFilters(to))
      .then(() => {
        store.commit('breadcrumbs/set', [{ text: 'Partnerzy', disabled: true }]);
        next();
      })
      .catch(() => store.dispatch('notifyError', { title: 'Nie udało się pobrać listy partnerów.' }));
  },
  meta: { toolbarTitle: 'Partnerzy karty miejskiej' }
};
export const partnerDetailsRoute: RouteConfig = {
  path: 'partners/:id(\\d+)',
  component: PartnerView,
  beforeEnter: (to, from, next) => {
    const partnerId = parseInt(to.params.id);

    store.commit('partners/setId', partnerId);

    store
      .dispatch('partners/get')
      .then(res => {
        const crumbs: any[] = [{ text: res.name, disabled: true }];
        if (isOfficial()) {
          crumbs.unshift({ text: 'Partnerzy', to: { name: 'partners' }, exact: true });
        }
        store.commit('breadcrumbs/set', crumbs);
      })
      .then(() => next())
      .catch(() => store.dispatch('notifyError', { title: 'Nie udało się pobrać danych Partnera' }));
  },
  children: [
    {
      path: '',
      name: 'partner',
      component: PartnerProfile,
      meta: { toolbarTitle: 'Profil partnera' }
    },
    {
      path: 'benefits',
      name: 'partnerBenefits',
      component: Benefits,
      beforeEnter: (to, from, next) => {
        const partnerId = parseInt(to.params.id);

        Promise.all([
          store.dispatch('settings/settingFindPublic', 'BENEFIT_CONFIRM'),
          store.dispatch('settings/settingFindPublic', PublicSettings.NO_GROUP_SELECTED_NAME),
          store.dispatch('groups/list', { count: -1 }),
          store.dispatch('categories/list', { count: -1, withInactive: true }),
          store.dispatch('partners/places/list', { count: -1, partnerId }),
          store.dispatch('partners/benefits/list', { count: -1, partnerId, withInactiveCategories: true })
        ])
          .then(() => next())
          .catch(() => store.dispatch('notifyError', { title: 'Nie udało się pobrać danych Partnera' }));
      },
      meta: { toolbarTitle: 'Profil partnera' }
    },
    {
      path: 'usage',
      name: 'partnerUsage',
      component: BenefitsHistory,
      props: { getPartner: true },
      beforeEnter: (to, from, next) => {
        const partnerId = parseInt(to.params.id);

        Promise.all([
          store.dispatch('benefitsHistory/list', { ...baseFilters(to), partnerId }),
          store.dispatch('access/check', ['PROFILE.GET'])
        ])
          .then(() => next())
          .catch(() => store.dispatch('notifyError', { title: 'Nie udało się pobrać danych Partnera' }));
      },
      meta: { toolbarTitle: 'Profil partnera' }
    },
    {
      path: 'history',
      name: 'partnerHistory',
      component: EntityHistory,
      props: { getPartner: true },
      beforeEnter: (to, from, next) => {
        store
          .dispatch('entityHistory/list', {
            ...baseFilters(to),
            tableName: 'Partner',
            entityId: to.params.id
          })
          .then(() => next())
          .catch(() => store.dispatch('notifyError', { title: 'Nie udało się pobrać danych Partnera' }));
      },
      meta: { toolbarTitle: 'Profil partnera' }
    },
    {
      path: 'places',
      name: 'partnerPlaces',
      component: Places,
      meta: { toolbarTitle: 'Profil partnera' },
      beforeEnter: (to, from, next) => {
        Promise.all([
          store.dispatch('partners/places/list', { ...baseFilters(to), partnerId: parseInt(to.params.id) }),
          store.dispatch('settings/settingFindPublic', 'PUBLIC_DEFAULT_CENTER'),
          store.dispatch('settings/settingFindPublic', 'PUBLIC_DEFAULT_MAP_LEVEL')
        ])
          .then(() => next())
          .catch(err => store.dispatch('notifyError', { title: 'Nie udało się pobrać listy obiektów.' }));
      }
    }
  ]
};

export const partnerEmployeesRoute: RouteConfig = {
  path: 'partners/:id(\\d+)/employees',
  name: 'employeesById',
  component: PartnerEmployees,
  beforeEnter: (to, from, next) => {
    const partnerId = parseInt(to.params.id);

    store.commit('partners/setId', partnerId);

    store
      .dispatch('partners/get')
      .then(res => {
        store.dispatch('employees/list', baseFilters(to)).then(() => {
          store.commit('breadcrumbs/set', [
            { text: 'Partnerzy', to: { name: 'partners' }, exact: true },
            { text: res.name, to: { name: 'partner', params: { id: partnerId } }, exact: true },
            { text: 'Pracownicy', disabled: true }
          ]);

          next();
        });
      })
      .catch(() => store.dispatch('notifyError', { title: 'Nie udało się pobrać danych pracownika' }));
  },
  meta: { toolbarTitle: 'Pracownicy' }
};

export const partnerEmployeeRoute: RouteConfig = {
  path: 'partners/:id(\\d+)/employees/:employeeId',
  name: 'employeeProfile',
  component: EmployeeProfile,
  beforeEnter: (to, from, next) => {
    const partnerId = parseInt(to.params.id);

    store.commit('partners/setId', partnerId);
    store.commit('employees/setId', to.params.employeeId);

    Promise.all([store.dispatch('employees/get'), store.dispatch('partners/get')])
      .then(([res, partnerRes]) => {
        store.commit('breadcrumbs/set', [
          { text: 'Partnerzy', to: { name: 'partners' }, exact: true },
          { text: partnerRes.name, to: { name: 'partner', params: { id: partnerId } }, exact: true },
          { text: 'Pracownicy', to: { name: 'employeesById', params: { id: partnerId } }, exact: true },
          { text: res.firstName + ' ' + res.lastName, disabled: true }
        ]);
        next();
      })
      .catch(() => store.dispatch('notifyError', { title: 'Nie udało się pobrać danych pracownika' }));
  },
  meta: { toolbarTitle: 'Pracownik' }
};
export const partnerEmployeeRolesRoute: RouteConfig = {
  path: 'partners/:id(\\d+)/employees/roles/:employeeId',
  name: 'employeeOfficialRoles',
  component: UserRoles,
  beforeEnter: (to, from, next) => {
    const partnerId = parseInt(to.params.id);

    store.commit('partners/setId', partnerId);
    store.commit('employees/setId', to.params.employeeId);

    Promise.all([
      store.dispatch(`profiles/userRole/list`, to.params.employeeId),
      store.dispatch(`roles/list`, { count: 0 }),
      Promise.all([store.dispatch('employees/get'), store.dispatch('partners/get')]).then(([res, partnerRes]) => {
        store.commit('breadcrumbs/set', [
          { text: 'Partnerzy', to: { name: 'partners' }, exact: true },
          { text: partnerRes.name, to: { name: 'partner', params: { id: partnerId } }, exact: true },
          { text: 'Pracownicy', to: { name: 'employeesById', params: { id: partnerId } }, exact: true },
          { text: res.firstName + ' ' + res.lastName, disabled: true },
          { text: 'Role', disabled: true }
        ]);
      })
    ])
      .then(() => next())
      .catch(error => {
        store.dispatch('notifyError', { title: 'Nie udało się pobrać danych pracownika' });
      });
  },
  meta: { toolbarTitle: 'Pracownik' }
};
